import { APIEndpoints } from '@/@admin/types/constants';
import {
    AdminToolsParams,
    CreateReleaseNoteDto,
    ReleaseNoteEntity,
    UpdateReleaseNoteDto,
    UpdateResponse,
} from '@/@admin/types/entities';
import { ERROR_MESSAGES } from '@/@cms/types';
import { tracking } from '@/lib/features/tracking';
import { portalApi } from '@/lib/http/axios';
import { BaseService } from './base.service';

/**
 * Service class for admin release notes operations
 * Handles CRUD, publish, and soft delete for release notes
 */
export class ReleaseNotesService extends BaseService {
    constructor() {
        super(portalApi);
    }

    /** List release notes with pagination, filtering, and search */
    async getReleaseNotes(params?: AdminToolsParams): Promise<{
        data: ReleaseNoteEntity[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
        };
    }> {
        try {
            const { data: response } = await this.axiosInstance.get(
                APIEndpoints.ADMIN_RELEASE_NOTES,
                { params },
            );

            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(response.message || 'Release notes fetch failed.'),
                    'admin.ReleaseNotesService.getReleaseNotes',
                );
                throw new Error(response.message || 'Release notes fetch failed.');
            }

            return {
                data: (response?.data ?? []) as ReleaseNoteEntity[],
                pagination: response?.pagination ?? {
                    page: 1,
                    limit: 20,
                    total: 0,
                    totalPages: 0,
                },
            };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get release notes: ${errorMessage}`),
                'admin.ReleaseNotesService.getReleaseNotes',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to get release notes: ${errorMessage}`),
            );
        }
    }

    /** Get a single release note by ID */
    async getReleaseNoteById(id: number): Promise<ReleaseNoteEntity> {
        try {
            const { data: response } = await this.axiosInstance.get(
                `${APIEndpoints.ADMIN_RELEASE_NOTES}/${id}`,
            );

            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(response.message || 'Release note fetch failed.'),
                    'admin.ReleaseNotesService.getReleaseNoteById',
                );
                throw new Error(response.message || 'Release note fetch failed.');
            }

            return response as ReleaseNoteEntity;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get release note: ${errorMessage}`),
                'admin.ReleaseNotesService.getReleaseNoteById',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to get release note: ${errorMessage}`),
            );
        }
    }

    /** Create a new release note (draft or scheduled) */
    async createReleaseNote(data: CreateReleaseNoteDto): Promise<ReleaseNoteEntity> {
        try {
            const { data: response } = await this.axiosInstance.post(
                APIEndpoints.ADMIN_RELEASE_NOTES,
                data,
            );

            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(response.message || 'Release note creation failed.'),
                    'admin.ReleaseNotesService.createReleaseNote',
                );
                throw new Error(response.message || 'Release note creation failed.');
            }

            this.clearCache();
            return response as ReleaseNoteEntity;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to create release note: ${errorMessage}`),
                'admin.ReleaseNotesService.createReleaseNote',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to create release note: ${errorMessage}`),
            );
        }
    }

    /** Update an existing release note (partial update) */
    async updateReleaseNote(id: number, data: UpdateReleaseNoteDto): Promise<ReleaseNoteEntity> {
        try {
            const { data: response } = await this.axiosInstance.patch(
                `${APIEndpoints.ADMIN_RELEASE_NOTES}/${id}`,
                data,
            );

            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(response.message || 'Release note update failed.'),
                    'admin.ReleaseNotesService.updateReleaseNote',
                );
                throw new Error(response.message || 'Release note update failed.');
            }

            this.clearCache();
            return response as ReleaseNoteEntity;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to update release note: ${errorMessage}`),
                'admin.ReleaseNotesService.updateReleaseNote',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to update release note: ${errorMessage}`),
            );
        }
    }

    /** Publish or schedule a release note. Omit publishedAt to publish immediately. */
    async publishReleaseNote(id: number, publishedAt?: string): Promise<ReleaseNoteEntity> {
        try {
            const { data: response } = await this.axiosInstance.post(
                `${APIEndpoints.ADMIN_RELEASE_NOTES}/${id}/publish`,
                publishedAt ? { publishedAt } : {},
            );

            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(response.message || 'Release note publish failed.'),
                    'admin.ReleaseNotesService.publishReleaseNote',
                );
                throw new Error(response.message || 'Release note publish failed.');
            }

            this.clearCache();
            return response as ReleaseNoteEntity;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to publish release note: ${errorMessage}`),
                'admin.ReleaseNotesService.publishReleaseNote',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to publish release note: ${errorMessage}`),
            );
        }
    }

    /** Soft-delete a release note (sets isActive to false) */
    async deleteReleaseNote(id: number): Promise<UpdateResponse> {
        try {
            const { data: response } = await this.axiosInstance.delete(
                `${APIEndpoints.ADMIN_RELEASE_NOTES}/${id}`,
            );

            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(response.message || 'Release note deletion failed.'),
                    'admin.ReleaseNotesService.deleteReleaseNote',
                );
                throw new Error(response.message || 'Release note deletion failed.');
            }

            this.clearCache();
            return response as UpdateResponse;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to delete release note: ${errorMessage}`),
                'admin.ReleaseNotesService.deleteReleaseNote',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to delete release note: ${errorMessage}`),
            );
        }
    }

    /** Presigned POST for a PNG or JPEG. The file is checked by its header after upload. */
    async createImageUpload(): Promise<{
        key: string;
        url: string;
        fields: Record<string, string>;
    }> {
        const { data } = await this.axiosInstance.post(
            `${APIEndpoints.ADMIN_RELEASE_NOTES}/images`,
        );
        return data;
    }

    async confirmImage(key: string): Promise<{ key: string; previewUrl: string }> {
        const { data } = await this.axiosInstance.post(
            `${APIEndpoints.ADMIN_RELEASE_NOTES}/images/confirm`,
            { key },
        );
        return data;
    }
}

export const releaseNotesService = new ReleaseNotesService();
